// (c) chaehongee. Confidential and proprietary.

import { createSlice } from '@reduxjs/toolkit'
import type { PayloadAction } from '@reduxjs/toolkit'

export interface CanvasPosition {
    x: number
    y: number
    w: number
    h: number
    manuallySet: boolean
    timestamp: number
}

interface CanvasPositionsState {
    // Keyed by sessionId -> nodeId -> position
    positions: Record<string, Record<string, CanvasPosition>>
    // Last known server snapshot for comparisons
    serverPositionsBySession: Record<string, Record<string, CanvasPosition>>
}

const initialState: CanvasPositionsState = {
    positions: {},
    serverPositionsBySession: {},
}

const canvasPositionsSlice = createSlice({
    name: 'canvasPositions',
    initialState,
    reducers: {
        setPosition: (
            state,
            action: PayloadAction<{
                sessionId: string
                nodeId: string
                position: Omit<CanvasPosition, 'timestamp'>
            }>
        ) => {
            const { sessionId, nodeId, position } = action.payload
            if (!state.positions[sessionId]) {
                state.positions[sessionId] = {}
            }
            state.positions[sessionId][nodeId] = {
                ...position,
                timestamp: Date.now(),
            }
        },
        setPositions: (
            state,
            action: PayloadAction<{
                sessionId: string
                positions: Record<string, CanvasPosition>
            }>
        ) => {
            const { sessionId, positions } = action.payload
            state.positions[sessionId] = positions
        },
        setPositionsFromServer: (
            state,
            action: PayloadAction<{
                sessionId: string
                positions: Record<string, CanvasPosition>
            }>
        ) => {
            const { sessionId, positions } = action.payload
            state.positions[sessionId] = positions
            state.serverPositionsBySession[sessionId] = positions
        },
        clearSessionPositions: (state, action: PayloadAction<string>) => {
            state.positions[action.payload] = {}
            state.serverPositionsBySession[action.payload] = {}
        },
        clearAllPositions: (state) => {
            state.positions = {}
            state.serverPositionsBySession = {}
        },
    },
})

export const {
    setPosition,
    setPositions,
    setPositionsFromServer,
    clearSessionPositions,
    clearAllPositions,
} = canvasPositionsSlice.actions
export default canvasPositionsSlice.reducer
